import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { UserPlus, Trash2, Shield, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { getAuthHeaders, user: currentUser } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/auth/register`, formData, {
        headers: getAuthHeaders()
      });
      
      toast.success('User created successfully!');
      setShowForm(false);
      setFormData({ email: '', password: '', name: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeaders()
      });
      
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay"></div>
      
      <div className="relative z-10 ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2">
                <span className="text-[#f97316]">User</span> Management
              </h1>
              <p className="text-[#94a3b8] text-sm uppercase tracking-widest">
                Add and Manage Authorized Users
              </p>
            </div>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="millmetrix-button bg-[#10b981] hover:bg-[#059669] text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {showForm ? 'Cancel' : 'Add User'}
            </Button>
          </div>

          {showForm && (
            <Card className="millmetrix-card p-6 mb-8">
              <h2 className="text-2xl font-bold tracking-tight mb-6 text-[#f8fafc]">Create New User</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="john@example.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="Minimum 8 characters"
                      required
                      minLength={8}
                    />
                  </div>

                  <div>
                    <Label htmlFor="role" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Role
                    </Label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="millmetrix-input mt-2 w-full"
                      required
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                    className="border-[#94a3b8] text-[#94a3b8]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="millmetrix-button bg-[#10b981] hover:bg-[#059669] text-white"
                  >
                    Create User
                  </Button>
                </div>
              </form>
            </Card>
          )}

          <Card className="millmetrix-card p-6">
            <h2 className="text-2xl font-bold tracking-tight mb-6 text-[#f8fafc]">All Users</h2>
            
            {loading ? (
              <div className="text-center py-12 text-[#94a3b8]">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-[#94a3b8]">No users found</div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-[#1e293b] p-4 rounded-md border border-[rgba(255,255,255,0.05)] hover:border-[#f97316] transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${
                          user.role === 'admin' ? 'bg-[#f97316]' : 'bg-[#0ea5e9]'
                        }`}>
                          {user.role === 'admin' ? (
                            <Shield className="w-5 h-5 text-white" />
                          ) : (
                            <User className="w-5 h-5 text-white" />
                          )}
                        </div>
                        
                        <div>
                          <h3 className="text-lg font-semibold text-[#f8fafc] flex items-center gap-2">
                            {user.name}
                            {user.id === currentUser?.id && (
                              <span className="text-xs bg-[#10b981] text-white px-2 py-1 rounded uppercase">
                                You
                              </span>
                            )}
                          </h3>
                          <p className="text-sm text-[#94a3b8]">{user.email}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <span className={`text-xs font-mono uppercase px-3 py-1 rounded ${
                          user.role === 'admin' 
                            ? 'bg-[#f97316] text-white' 
                            : 'bg-[#0ea5e9] text-white'
                        }`}>
                          {user.role}
                        </span>
                        
                        {user.id !== currentUser?.id && (
                          <Button
                            onClick={() => handleDelete(user.id)}
                            variant="outline"
                            size="sm"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Users;
